import { ChangeEvent, useEffect, useState } from 'react';

import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Grid,
  Popover,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

import DrawerHeader from 'components/DrawerHeader';
import ImageViewer from 'components/ImageViewer';
import StyledChannelChipIcon from 'components/StyledChannelChipIcon';
import TabPanel from 'components/TabPanel';
import TabPanelDocumentos from 'components/TabPanelDocumentos';
import TabPanelMidias from 'components/TabPanelMidias';
import { TipoCanalEnum } from 'store/reducers/canais';
import { GrupoInterface } from 'store/reducers/grupos';

import TabPanelListaMembros from './TabPanelListaMembros';

type DrawerDetalhesAtendimentoProps = {
  open: boolean;
  onClose: () => void;
  grupoSelecionado: GrupoInterface;
};

const useStyles = makeStyles(({ appDrawer, palette, spacing }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarLarge: {
    width: spacing(10),
    height: spacing(10),
  },
  popover: {
    width: spacing(24),
    maxHeight: spacing(24),
    padding: spacing(0.5, 1.5, 1.5),
  },
  tabs: {
    borderBottom: `1px solid ${palette.divider}`,
  },
}));

export default function DrawerDetalhesAtendimento(
  props: DrawerDetalhesAtendimentoProps,
) {
  const { open, onClose, grupoSelecionado } = props;
  const classes = useStyles();
  const { palette } = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'canaisGrupopopover',
  });

  const grupoChatCanaisAtivos = grupoSelecionado?.grupoChatCanais?.filter(
    c => c.isAtivo,
  );

  const handleTabChange = (
    event: ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setTabValue(newValue);
  };

  // Volta para a Tab inicial sempre que abrir o Drawer
  useEffect(() => {
    if (open) setTabValue(0);
  }, [open]);

  const renderCanais = grupoChatCanaisAtivos?.map(canal => (
    <Box key={canal.id} style={{ marginTop: 8 }}>
      <StyledChannelChipIcon
        color={canal.isCanalOnline ? undefined : palette.error.main}
        type={canal.tipoCanal as TipoCanalEnum}
        label={canal.nome}
      />
    </Box>
  ));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
    >
      <DrawerHeader title="Dados do grupo" onClose={onClose} />

      <Box p={2}>
        <Grid container alignItems="center" spacing={3}>
          <Grid item>
            <ImageViewer src={grupoSelecionado?.foto}>
              <Avatar
                src={grupoSelecionado?.foto}
                className={classes.avatarLarge}
              />
            </ImageViewer>
          </Grid>

          <Grid item xs zeroMinWidth container wrap="nowrap" direction="column">
            <Grid item>
              <Typography variant="body2" noWrap>
                {grupoSelecionado?.nome}
              </Typography>
            </Grid>

            <Grid item>
              {grupoSelecionado &&
                Array.isArray(grupoChatCanaisAtivos) &&
                grupoChatCanaisAtivos.length > 0 && (
                  <ButtonBase {...bindTrigger(popupState)} disableRipple>
                    <Typography variant="body2" color="textSecondary">
                      Lista de canais
                    </Typography>
                    <ArrowDropDownIcon />
                  </ButtonBase>
                )}

              {grupoSelecionado &&
                Array.isArray(grupoChatCanaisAtivos) &&
                grupoChatCanaisAtivos.length === 0 && (
                  <Typography variant="subtitle2" color="textSecondary">
                    Não há canais disponíveis
                  </Typography>
                )}

              <Popover
                {...bindPopover(popupState)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                classes={{ paper: classes.popover }}
              >
                {renderCanais}
              </Popover>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={classes.tabs}
        variant="fullWidth"
        centered
      >
        <Tab label="Membros" />
        <Tab label="Mídia" />
        <Tab label="Documentos" />
      </Tabs>

      <TabPanel index={0} value={tabValue}>
        <TabPanelListaMembros />
      </TabPanel>

      <TabPanel index={1} value={tabValue}>
        <TabPanelMidias
          midiaItemHeight={84}
          midiaItemWidth={84}
          grupoChatId={grupoSelecionado?.id}
          isGroup
        />
      </TabPanel>

      <TabPanel index={2} value={tabValue}>
        <TabPanelDocumentos grupoChatId={grupoSelecionado?.id} isGroup />
      </TabPanel>
    </Drawer>
  );
}
